import React, { FormEvent, useEffect, useRef, useState } from 'react';

interface ContactFormData {
  name: string;
  phone: string;
  email: string;
  subject: string;
  message: string;
}

const Contactus: React.FC = () => {
  const [isVisible, setIsVisible] = useState({
    leftColumn: false,
    rightColumn: false,
    contactCards: false,
    form: false
  });
  
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    phone: '',
    email: '',
    subject: '',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const leftColumnRef = useRef<HTMLDivElement>(null);
  const rightColumnRef = useRef<HTMLDivElement>(null);
  const contactCardsRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLDivElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage(null);

    try {
      // Prepare data for WhatsApp and Email
      const whatsappNumber = '919975922007'; // Added country code 91
      const emailAddress = '';
      
      // Format message for WhatsApp
      const whatsappMessage = `
*New Contact Form Submission:*

*Name:* ${formData.name}
*Phone:* ${formData.phone}
*Email:* ${formData.email}
*Subject:* ${formData.subject}
*Message:* ${formData.message}
      `.trim();

      // Format message for Email
      const emailSubject = `Contact Form: ${formData.subject}`;
      const emailBody = `
You have received a new contact form submission:

Name: ${formData.name}
Phone: ${formData.phone}
Email: ${formData.email}
Subject: ${formData.subject}

Message:
${formData.message}

Submitted on: ${new Date().toLocaleString()}
      `.trim();

      // Open WhatsApp with pre-filled message
      const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`;
      window.open(whatsappUrl, '_blank');
      
      // Open default email client with pre-filled data
      const mailtoUrl = `mailto:${emailAddress}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
      window.location.href = mailtoUrl;

      setSubmitMessage({
        type: 'success',
        message: 'Thank you! Your message is being sent via WhatsApp and Email.'
      });
      
      // Reset form after a short delay
      setTimeout(() => {
        setFormData({
          name: '',
          phone: '',
          email: '',
          subject: '',
          message: ''
        });
      }, 2000);

    } catch (error: any) {
      setSubmitMessage({
        type: 'error',
        message: 'Failed to send message. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const target = entry.target as HTMLElement;
            const animationType = target.dataset.animation;
            
            if (animationType) {
              setIsVisible(prev => ({
                ...prev,
                [animationType]: true
              }));
            }
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    const refs = [
      { ref: leftColumnRef, key: 'leftColumn' },
      { ref: rightColumnRef, key: 'rightColumn' },
      { ref: contactCardsRef, key: 'contactCards' },
      { ref: formRef, key: 'form' }
    ];

    refs.forEach(({ ref, key }) => {
      if (ref.current) {
        ref.current.dataset.animation = key;
        observer.observe(ref.current);
      }
    });

    return () => {
      refs.forEach(({ ref }) => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      });
    };
  }, []);

  return (
    <div className="font-sans bg-gradient-to-br from-white to-gray-50 min-h-screen text-gray-600" style={{ fontFamily: 'Lato, sans-serif' }}>
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left Column - Enhanced Contact Information */}
          <div 
            ref={leftColumnRef}
            className={`space-y-8 transition-all duration-700 ${
              isVisible.leftColumn 
                ? 'animate-slide-in-left opacity-100' 
                : 'opacity-0'
            }`}
          >
            {/* Header with two-color title */}
            <div className="text-center lg:text-left">
              <h2 className="text-5xl font-bold mb-4">
                <i className="fas fa-headset mr-4 text-teal-500"></i>
                <span className="text-gray-900">Contact</span>
                <span className="text-amber-500"> Us</span>
              </h2>
              <div className="w-24 h-1.5 bg-gradient-to-r from-teal-500 to-amber-400 rounded-full mx-auto lg:mx-0"></div>
              <p className="text-gray-500 mt-4 text-lg">
                <i className="fas fa-comments mr-2 text-teal-500"></i>
                Get in touch with us for any inquiries
              </p>
            </div>

            {/* Contact Cards */}
            <div 
              ref={contactCardsRef}
              className={`space-y-6 transition-all duration-700 delay-300 ${
                isVisible.contactCards 
                  ? 'animate-slide-in-bottom opacity-100' 
                  : 'opacity-0'
              }`}
            >
              {/* Visit my Place */}
              <div className="bg-white p-4 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="flex flex-col space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-teal-400 rounded-xl flex items-center justify-center shadow-md">
                    <i className="fas fa-map-marker-alt text-white text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-800 mb-3 pt-3">Visit Our Place</h3>
                    <p className="text-gray-600 leading-relaxed text-sm">
                      RATANLAL TRADERS POST–WALCHANDNAGAR NEW BAZAR PETH PIN–413114
                    </p>
                  </div>
                </div>
              </div>

              {/* Office Schedule */}
              <div className="bg-white p-4 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 delay-100">
                <div className="flex flex-col space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-400 rounded-xl flex items-center justify-center shadow-md">
                    <i className="fas fa-clock text-white text-lg"></i>
                  </div>
                  <div>
                    <h3 className=" font-bold text-gray-800 mb-3 text-xl pt-3">Office Schedule</h3>
                    <div className="text-gray-600 space-y-2 text-lg">
                      <p className='text-sm'>
                        <i className="fas fa-calendar-day text-teal-500 mr-2"></i>
                        <span className="font-semibold text-gray-700 text-sm">Monday to Saturday:</span> 9AM - 5PM
                      </p>
                      <p className='text-sm'>
                        <i className="fas fa-calendar-week text-teal-500 mr-2"></i>
                        <span className="font-semibold text-gray-700 text-sm">Sunday:</span> 9AM - 12PM
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Us */}
              <div className="bg-white p-4 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 delay-200">
                <div className="flex flex-col items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-400 rounded-xl flex items-center justify-center shadow-md">
                    <i className="fas fa-phone-alt text-white text-lg"></i>
                  </div>
                  <div>
                    <h3 className=" font-bold text-gray-800 mb-3 text-xl pt-3">Contact Us</h3>
                    <div className="text-gray-600 space-y-3 text-lg">
                      <p className="flex items-center space-x-2">
                        <i className="fas fa-mobile-alt text-teal-500"></i>
                        <span className="font-semibold text-gray-700 text-sm">Phone:</span>
                        <span className="text-teal-600 font-medium text-sm">+91 9975922007</span>
                      </p>
                      <p className="flex items-center space-x-2 ">
                        <i className="fas fa-envelope text-teal-500"></i>
                        <span className="font-semibold text-gray-700 text-sm">Email:</span>
                        <span className="text-teal-600 font-medium text-sm">doshi.santosh@rediffmail.com</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Enhanced Contact Form */}
          <div 
            ref={rightColumnRef}
            className={`space-y-8 transition-all duration-700 ${
              isVisible.rightColumn 
                ? 'animate-slide-in-right opacity-100' 
                : 'opacity-0'
            }`}
          >
            {/* Contact Form */}
            <div 
              ref={formRef}
              className={`bg-white p-8 rounded-3xl shadow-2xl border border-gray-100 transition-all duration-700 delay-500 ${
                isVisible.form 
                  ? 'animate-slide-in-top opacity-100' 
                  : 'opacity-0'
              }`}
            >
              <div className="text-center mb-8">
                <h3 className="text-4xl font-bold mb-3">
                  <i className="fas fa-paper-plane mr-3 text-teal-500"></i>
                  <span className="text-gray-800">Send Your</span>
                  <span className="text-amber-500"> Message</span>
                </h3>
                <p className="text-gray-500 text-lg">
                  <i className="fas fa-reply mr-2 text-teal-500"></i>
                  We'll get back to you within 24 hours
                </p>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="block text-gray-700 font-semibold text-lg">
                      <i className="fas fa-user text-teal-500 mr-2"></i>
                      Name
                    </label>
                    <input 
                      type="text" 
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-6 py-4 border-2 border-gray-200 rounded-2xl focus:outline-none focus:border-teal-500 focus:ring-4 focus:ring-teal-500/20 transition-all duration-300 bg-gray-50/50 pl-12"
                      placeholder="Your Name"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-gray-700 font-semibold text-lg">
                      <i className="fas fa-mobile-alt text-teal-500 mr-2"></i>
                      Phone
                    </label>
                    <input 
                      type="tel" 
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-6 py-4 border-2 border-gray-200 rounded-2xl focus:outline-none focus:border-teal-500 focus:ring-4 focus:ring-teal-500/20 transition-all duration-300 bg-gray-50/50 pl-12"
                      placeholder="Your Phone"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="block text-gray-700 font-semibold text-lg">
                      <i className="fas fa-envelope text-teal-500 mr-2"></i>
                      Email
                    </label>
                    <input 
                      type="email" 
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-6 py-4 border-2 border-gray-200 rounded-2xl focus:outline-none focus:border-teal-500 focus:ring-4 focus:ring-teal-500/20 transition-all duration-300 bg-gray-50/50 pl-12"
                      placeholder="Your Email"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-gray-700 font-semibold text-lg">
                      <i className="fas fa-tag text-teal-500 mr-2"></i>
                      Subject
                    </label>
                    <input 
                      type="text" 
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="w-full px-6 py-4 border-2 border-gray-200 rounded-2xl focus:outline-none focus:border-teal-500 focus:ring-4 focus:ring-teal-500/20 transition-all duration-300 bg-gray-50/50 pl-12"
                      placeholder="Subject"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-gray-700 font-semibold text-lg">
                    <i className="fas fa-comment-dots text-teal-500 mr-2"></i>
                    Message
                  </label>
                  <textarea 
                    rows={5}
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-6 py-4 border-2 border-gray-200 rounded-2xl focus:outline-none focus:border-teal-500 focus:ring-4 focus:ring-teal-500/20 transition-all duration-300 bg-gray-50/50 resize-none pl-12"
                    placeholder="Your Message..."
                    required
                  ></textarea>
                </div>

                {submitMessage && (
                  <div className={`p-4 mb-4 rounded-xl text-sm ${
                    submitMessage.type === 'success' 
                      ? 'bg-green-50 text-green-800 border-2 border-green-200' 
                      : 'bg-red-50 text-red-800 border-2 border-red-200'
                  }`}>
                    <div className="flex items-center">
                      <i className={`mr-2 fas ${submitMessage.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}`}></i>
                      <p>{submitMessage.message}</p>
                    </div>
                  </div>
                )}
                
                <button 
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-amber-500 to-amber-400 text-white py-5 px-8 rounded-2xl font-bold text-lg hover:from-teal-600 hover:to-teal-500 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-teal-500/30 flex items-center justify-center space-x-3 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  <i className={`fas ${isSubmitting ? 'fa-spinner fa-spin' : 'fa-paper-plane'}`}></i>
                  <span>{isSubmitting ? 'SENDING...' : 'SEND MESSAGE'}</span>
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contactus;